import {
  FEATURES,
  IGroupInfo,
  IPendingReferral,
  IUserInfo,
} from "@home-health-notify/domain";
import { Box, Divider, ListItemAvatar, Typography } from "@mui/material";
import {
  dateAgoTime,
  OrganizationContext,
  sortByDate,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import { useContext } from "react";
import DisplayAvatarImage from "../../../common/DisplayAvatarImage";
import { RightDrawerContext } from "../../../context/RightDrawerContext";
import { MemberListItem, MemberListItemButton } from "./Members.style";
import { RevokeReferral } from "./RevokeReferral";

const MembersPendingReferrals = ({
  groupInfo,
  goToChat,
}: {
  groupInfo: IGroupInfo;
  goToChat: (userInfo: IUserInfo) => void;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const { closeDrawer } = useContext(RightDrawerContext);
  const { userDirectory } = useContext(UserDirectoryContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const hasRevokeReferralAccess = selectedOrgUser?.hasAccess(
    FEATURES.REVOKE_REFERRALS,
  );
  const isMember = groupInfo.isMember;
  const canRevokeReferrals = hasRevokeReferralAccess && isMember;

  const openChat = (userInfo: IUserInfo) => {
    goToChat(userInfo);
    closeDrawer();
  };

  const sortedPendingReferrals = sortByDate<IPendingReferral>(
    groupInfo.pendingReferrals || [],
  );

  return (
    <>
      {sortedPendingReferrals?.length > 0 && (
        <Box data-testid="pending-referral-component">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 24px",
            }}
          >
            <Typography data-testid="pending-referrals-header">
              {tNs("pendingReferrals")} ({sortedPendingReferrals.length})
            </Typography>
          </Box>
          <Divider />

          <Box style={{ padding: "8px 0" }}>
            {sortedPendingReferrals.map(pendingReferral => {
              const userInfo = userDirectory.getUserInfo(
                pendingReferral.userId,
              );
              return (
                <Box
                  sx={{ maxHeight: "70dvh", overflow: "auto" }}
                  key={pendingReferral.userId}
                >
                  {userInfo && (
                    <Box
                      sx={{ margin: "0px 16px" }}
                      data-testid="pending-referrals-list"
                    >
                      <MemberListItem
                        key={pendingReferral.userId}
                        disablePadding
                        secondaryAction={
                          canRevokeReferrals && (
                            <RevokeReferral
                              userInfo={userInfo}
                              groupInfo={groupInfo}
                            />
                          )
                        }
                      >
                        <MemberListItemButton
                          data-testid="pending-referral"
                          onClick={
                            userInfo.status !== "DELETED"
                              ? () => openChat(userInfo)
                              : undefined
                          }
                          disabled={userInfo.status === "DELETED"}
                        >
                          <Box sx={{
                            flexDirection: "column"
                          }}>
                            <ListItemAvatar sx={{ marginLeft: "10px" }}>
                              <DisplayAvatarImage
                                src={userInfo.image}
                                alt={userInfo.name}
                              />
                            </ListItemAvatar>
                          </Box>
                          <Box>
                            <Typography>
                              {userInfo.status === "DELETED"
                                ? `${userInfo.title} (${tNs("deleted")})`
                                : userInfo.title}
                            </Typography>
                            {pendingReferral.createdAt && (
                              <Typography
                                noWrap
                                sx={{ width: "250px", fontSize: "small" }}
                              >
                                {tNs("sent")}{" "}
                                {dateAgoTime(pendingReferral.createdAt)}
                              </Typography>
                            )}
                          </Box>
                        </MemberListItemButton>
                      </MemberListItem>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default MembersPendingReferrals;
